import React, { useState } from "react";
import client, { tryParseJSON } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Compass, Save } from "lucide-react";

const SUGGESTED = ["Faith", "Grace", "Prayer", "Forgiveness", "Wisdom", "Covenant", "Suffering", "Justice", "Hope", "Repentance"];

export default function Topical() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [entry, setEntry] = useState(null);
  const [raw, setRaw] = useState("");

  const lookup = async (e, presetTopic) => {
    e?.preventDefault();
    const t = (presetTopic || topic).trim();
    if (!t) return;
    if (!user) { toast.error("Sign in to use topical study"); nav("/login"); return; }
    setTopic(t);
    setBusy(true); setEntry(null); setRaw("");
    try {
      const r = await client.post("/study/naves", { topic: t });
      const parsed = tryParseJSON(r.data.raw);
      if (parsed) setEntry(parsed); else setRaw(r.data.raw);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  };

  const save = async () => {
    if (!entry) return;
    try {
      await client.post("/studies", {
        title: `${entry.topic} — Nave's`,
        kind: "topic",
        payload: entry,
      });
      toast.success("Study saved");
    } catch { toast.error("Save failed"); }
  };

  const parseRef = (ref) => {
    // Format: "Book Chapter:Verse" or "Book Chapter:Verse-Verse"
    const m = ref.match(/^([1-3]?\s?[A-Za-z ]+?)\s+(\d+):(\d+)/);
    return m ? { book: m[1].trim(), chapter: m[2], verse: m[3] } : null;
  };

  return (
    <div className="space-y-10" data-testid="topical-page">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-[#78350F] mb-4">Nave's-Style Topical Bible</div>
        <h1 className="font-serif-display text-5xl md:text-6xl text-stone-900 mb-3">Scripture by theme.</h1>
        <p className="text-stone-600 max-w-2xl">
          Type any biblical topic. The passages that speak to it — organized into sub-topics —
          will be gathered for you. AI-composed in the tradition of Nave's Topical Bible.
        </p>
      </div>

      <form onSubmit={lookup} className="border-t border-b hairline py-8">
        <label className="block text-xs uppercase tracking-[0.2em] text-stone-500 mb-3">Topic</label>
        <div className="flex flex-col md:flex-row gap-6 items-end">
          <input value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. faith, mercy, the kingdom of God"
            data-testid="topic-input"
            className="flex-1 bg-transparent border-b border-stone-300 focus:border-[#9A3B3B] focus:outline-none py-2 text-3xl font-serif-display" />
          <button type="submit" disabled={busy} data-testid="topic-submit"
            className="bg-[#9A3B3B] hover:bg-[#7F2F2F] text-white px-8 py-3 tracking-wide transition-colors disabled:opacity-60 flex items-center gap-2">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Compass size={16} strokeWidth={1.5} />}
            Compile
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <span className="text-xs uppercase tracking-[0.2em] text-stone-500 mr-2 self-center">Try:</span>
          {SUGGESTED.map((t) => (
            <button key={t} type="button" onClick={(e) => lookup(e, t)}
              data-testid={`suggest-${t.toLowerCase()}`}
              className="text-sm border hairline px-3 py-1 font-serif-display hover:border-stone-400 transition-colors">
              {t}
            </button>
          ))}
        </div>
      </form>

      {busy && <div className="text-center py-12 text-stone-500"><Loader2 className="mx-auto animate-spin" /> <div className="mt-3 italic">Gathering references...</div></div>}

      {entry && (
        <article className="space-y-10" data-testid="topic-entry">
          <header className="border hairline bg-[#FDFBF7] p-8 md:p-12">
            <div className="flex items-start justify-between gap-6">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">Topic</div>
                <h2 className="font-serif-display text-4xl md:text-5xl text-stone-900 mb-4">{entry.topic}</h2>
                {entry.summary && (
                  <p className="font-serif-display text-lg text-stone-700 italic leading-relaxed max-w-3xl">{entry.summary}</p>
                )}
              </div>
              <button onClick={save} data-testid="topic-save"
                className="text-sm text-[#2E4F4F] hover:text-[#213A3A] flex items-center gap-1 border border-[#2E4F4F] px-3 py-1.5">
                <Save size={13} strokeWidth={1.5} /> Save
              </button>
            </div>
          </header>

          <div className="space-y-8">
            {entry.subtopics?.map((s, i) => (
              <div key={i} className="border-t hairline pt-6">
                <div className="grid md:grid-cols-4 gap-6">
                  <div className="md:col-span-1">
                    <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{String(i + 1).padStart(2, "0")}</div>
                    <h3 className="font-serif-display text-2xl text-stone-900">{s.title}</h3>
                    {s.description && <p className="text-sm text-stone-600 mt-2">{s.description}</p>}
                  </div>
                  <ul className="md:col-span-3 space-y-2">
                    {s.references?.map((ref, j) => {
                      const p = parseRef(ref);
                      return (
                        <li key={j} className="font-mono text-sm">
                          {p ? (
                            <Link to={`/verse?book=${getBookId(p.book)}&chapter=${p.chapter}&verse=${p.verse}&translation=KJV`}
                              className="text-[#9A3B3B] hover:underline">
                              {ref}
                            </Link>
                          ) : <span className="text-stone-700">{ref}</span>}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            ))}
          </div>

          {entry.see_also?.length > 0 && (
            <section className="border-t hairline pt-6">
              <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-3">See also</div>
              <div className="flex flex-wrap gap-2">
                {entry.see_also.map((t) => (
                  <button key={t} onClick={(e) => lookup(e, t)}
                    className="text-sm border hairline px-3 py-1 font-serif-display hover:border-stone-400">
                    {t}
                  </button>
                ))}
              </div>
            </section>
          )}
        </article>
      )}

      {raw && !entry && (
        <pre className="border hairline bg-[#FDFBF7] p-6 text-sm whitespace-pre-wrap text-stone-700">{raw}</pre>
      )}
    </div>
  );
}

const BOOK_ID_MAP = {
  "genesis":1,"exodus":2,"leviticus":3,"numbers":4,"deuteronomy":5,"joshua":6,"judges":7,"ruth":8,
  "1 samuel":9,"2 samuel":10,"1 kings":11,"2 kings":12,"1 chronicles":13,"2 chronicles":14,"ezra":15,
  "nehemiah":16,"esther":17,"job":18,"psalms":19,"psalm":19,"proverbs":20,"ecclesiastes":21,
  "song of solomon":22,"song of songs":22,"isaiah":23,"jeremiah":24,"lamentations":25,"ezekiel":26,
  "daniel":27,"hosea":28,"joel":29,"amos":30,"obadiah":31,"jonah":32,"micah":33,"nahum":34,
  "habakkuk":35,"zephaniah":36,"haggai":37,"zechariah":38,"malachi":39,
  "matthew":40,"mark":41,"luke":42,"john":43,"acts":44,"romans":45,
  "1 corinthians":46,"2 corinthians":47,"galatians":48,"ephesians":49,"philippians":50,"colossians":51,
  "1 thessalonians":52,"2 thessalonians":53,"1 timothy":54,"2 timothy":55,"titus":56,"philemon":57,
  "hebrews":58,"james":59,"1 peter":60,"2 peter":61,"1 john":62,"2 john":63,"3 john":64,
  "jude":65,"revelation":66,
};
function getBookId(name) {
  return BOOK_ID_MAP[name.toLowerCase().trim()] || 1;
}
